import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createActivity, deleteActivity, updateActivity, updateActivityStatus } from "../../api/activities";

const useActivityMutations = () => {
  const queryClient = useQueryClient();

  // Centralizes activity mutations and the related activity-query invalidation.
  const { mutateAsync: createActivityMutation, isPending: isCreatingActivity } = useMutation({
    mutationFn: createActivity,
  });

  const { mutateAsync: updateActivityMutation, isPending: isUpdatingActivity } = useMutation({
    mutationFn: ({ activityId, payload }: { activityId: number; payload: Parameters<typeof updateActivity>[1] }) =>
      updateActivity(activityId, payload),
  });

  const { mutateAsync: updateActivityStatusMutation, isPending: isUpdatingActivityStatus } = useMutation({
    mutationFn: ({
      activityId,
      payload,
    }: {
      activityId: number;
      payload: Parameters<typeof updateActivityStatus>[1];
    }) => updateActivityStatus(activityId, payload),
  });

  const { mutateAsync: deleteActivityMutation, isPending: isDeletingActivity } = useMutation({
    mutationFn: (activityId: number) => deleteActivity(activityId),
  });

  const invalidateActivityQueries = async (contactPersonId?: number) => {
    if (contactPersonId) {
      await queryClient.invalidateQueries({ queryKey: ["contactPersonActivities", contactPersonId] });
    }
    await queryClient.invalidateQueries({ queryKey: ["activities"] });
  };

  return {
    createActivity: createActivityMutation,
    updateActivity: updateActivityMutation,
    updateActivityStatus: updateActivityStatusMutation,
    deleteActivity: deleteActivityMutation,
    isCreatingActivity,
    isUpdatingActivity,
    isUpdatingActivityStatus,
    isDeletingActivity,
    invalidateActivityQueries,
  };
};

export default useActivityMutations;
